import React, { createContext, useState, useContext, useEffect, useRef, useCallback } from 'react';
import { authAPI } from '../utils/api';
import { setUserContext, clearUserContext } from '@/lib/sentry';

const AuthContext = createContext(null);

const SESSION_TIMEOUT_MS = 30 * 60 * 1000; // 30 minutes
const LAST_ACTIVE_KEY    = 'last_active_at';

export const AuthProvider = ({ children }) => {
  const [user, setUser]       = useState(null);
  const [loading, setLoading] = useState(true);
  const timeoutRef            = useRef(null);

  const logout = useCallback(() => {
    localStorage.removeItem('token');
    localStorage.removeItem('user');
    localStorage.removeItem(LAST_ACTIVE_KEY);
    setUser(null);
    clearUserContext();
    if (timeoutRef.current) clearTimeout(timeoutRef.current);
  }, []);

  const resetInactivityTimer = useCallback(() => {
    localStorage.setItem(LAST_ACTIVE_KEY, Date.now().toString());
    if (timeoutRef.current) clearTimeout(timeoutRef.current);
    timeoutRef.current = setTimeout(() => {
      // Auto-logout after 30 min inactivity
      logout();
      // Same rule as the 401 handler: no token → no demo-domain override.
      try { localStorage.removeItem('demo_tenant_slug'); } catch { /* private mode */ }
      window.location.href = '/login?reason=session_timeout';
    }, SESSION_TIMEOUT_MS);
  }, [logout]);

  useEffect(() => {
    const token     = localStorage.getItem('token');
    const savedUser = localStorage.getItem('user');
    const lastActive = parseInt(localStorage.getItem(LAST_ACTIVE_KEY) || '0', 10);

    if (token && savedUser) {
      // Check if session already timed out
      if (lastActive && Date.now() - lastActive > SESSION_TIMEOUT_MS) {
        logout();
      } else {
        const parsed = JSON.parse(savedUser);
        setUser(parsed);
        setUserContext(parsed);
        resetInactivityTimer();
      }
    }
    setLoading(false);
  }, [logout, resetInactivityTimer]);

  // Listen for user activity to reset the timer
  useEffect(() => {
    if (!user) return;
    const events = ['mousedown', 'keydown', 'scroll', 'touchstart', 'click'];
    const handler = () => resetInactivityTimer();
    events.forEach(e => window.addEventListener(e, handler, { passive: true }));
    return () => events.forEach(e => window.removeEventListener(e, handler));
  }, [user, resetInactivityTimer]);

  const login = async (credentials) => {
    const { data } = await authAPI.login(credentials);

    // Special responses — do NOT store tokens, pass data through to Login.js
    if (data.requires_2fa || data.requires_ip_verification || data.password_expired) {
      return data;
    }

    // Normal login — store token and set user
    if (data.token) {
      localStorage.setItem('token', data.token);
      localStorage.setItem('user', JSON.stringify(data.user));
      localStorage.setItem('last_active_at', Date.now().toString());
      setUser(data.user);
      setUserContext(data.user);
      resetInactivityTimer();
    }
    return data.user;
  };

  const updateUser = (updatedUser) => {
    localStorage.setItem('user', JSON.stringify(updatedUser));
    setUser(updatedUser);
  };

  return (
    <AuthContext.Provider value={{ user, setUser: updateUser, login, logout, loading }}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within AuthProvider');
  }
  return context;
};
